import React, { useEffect, useState, useCallback, useMemo } from 'react';
import EntityTable from '../components/EntityTable';
import EntityDetailPanel from '../components/EntityDetailPanel';
import Toolbar, { Pager } from '../components/Toolbar';
import RelLink from '../components/RelLink';
import { Cards } from '../api/admin';

const PAGE_SIZE = 50;

const CardsTab = ({ filters, goTo }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(Number(filters.page) || 1);
    const [searchName, setSearchName] = useState(filters.name || '');
    const [supertype, setSupertype] = useState(filters.supertype || '');
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        setSearchName(filters.name || '');
        setSupertype(filters.supertype || '');
        setPage(Number(filters.page) || 1);
    }, [filters.name, filters.supertype, filters.page]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await Cards.list({
                page,
                count: PAGE_SIZE,
                name: searchName || undefined,
                supertype: supertype || undefined,
            });
            setRows(result.cards || []);
        } catch (e) {
            setError(e.message);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, searchName, supertype]);

    useEffect(() => { load(); }, [load]);

    // If filter.cardId is present, highlight the matching row in the current page;
    // do a one-off fetch trying to find it.
    useEffect(() => {
        if (!filters.cardId) return;
        const found = rows.find((r) => String(r.id) === String(filters.cardId));
        if (found) setSelected(found);
    }, [filters.cardId, rows]);

    const applySearch = () =>
        goTo({
            tab: 'cards',
            name: searchName || undefined,
            supertype: supertype || undefined,
            page: 1,
        });
    const clearFilters = () => goTo({ tab: 'cards' });

    const columns = useMemo(() => [
        { key: 'id', header: 'ID', width: '80px' },
        { key: 'name', header: 'Name' },
        { key: 'supertype', header: 'Supertype' },
        { key: 'hp', header: 'HP', width: '60px' },
        { key: 'rarity', header: 'Rarity' },
        { key: 'artist', header: 'Artist' },
        {
            key: 'set',
            header: 'Set',
            render: (r) =>
                r.set?.name ? (
                    <RelLink tab="sets" filters={{ setName: r.set.name }} goTo={goTo}>
                        {r.set.name}
                    </RelLink>
                ) : '—',
        },
    ], [goTo]);

    const sectionsFor = (r) => [
        {
            title: 'Card',
            rows: [
                { label: 'ID', value: r.id },
                { label: 'Name', value: r.name },
                { label: 'Supertype', value: r.supertype },
                { label: 'Subtypes', value: (r.subtypes || []).join(', ') || '—' },
                { label: 'HP', value: r.hp },
                { label: 'Rarity', value: r.rarity },
                { label: 'Artist', value: r.artist },
                { label: 'Number', value: r.number },
                { label: 'Level', value: r.level },
                { label: 'Flavor', value: r.flavor_text },
            ],
        },
        {
            title: 'Set',
            rows: [
                {
                    label: 'Name',
                    render: () =>
                        r.set?.name ? (
                            <RelLink tab="sets" filters={{ setName: r.set.name }} goTo={goTo}>
                                {r.set.name}
                            </RelLink>
                        ) : '—',
                },
                { label: 'Series', value: r.set?.series },
                { label: 'Release', value: r.set?.releaseDate ? new Date(r.set.releaseDate).toLocaleDateString() : '—' },
            ],
        },
        {
            title: 'Related',
            rows: [
                {
                    label: 'Listings of this card',
                    render: () => (
                        <RelLink tab="listings" filters={{ cardId: r.id }} goTo={goTo}>
                            View listings →
                        </RelLink>
                    ),
                },
                {
                    label: 'Certs of this card',
                    render: () => (
                        <RelLink tab="certs" filters={{ cardName: r.name }} goTo={goTo}>
                            View certs →
                        </RelLink>
                    ),
                },
            ],
        },
    ];

    return (
        <div className="admin-tab-panel">
            <Toolbar>
                <input
                    placeholder="Card name"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                />
                <select value={supertype} onChange={(e) => setSupertype(e.target.value)}>
                    <option value="">All supertypes</option>
                    <option value="pokemon">Pokémon</option>
                    <option value="trainer">Trainer</option>
                    <option value="energy">Energy</option>
                </select>
                <button className="btn-primary" onClick={applySearch}>Search</button>
                <button className="btn-ghost" onClick={clearFilters}>Clear</button>
            </Toolbar>

            {loading && <p className="loading">Loading…</p>}
            {error && <p className="error-message">Error: {error}</p>}

            {!loading && !error && (
                <EntityTable
                    columns={columns}
                    rows={rows}
                    onRowClick={(r) => setSelected(r)}
                    emptyMessage="No cards match these filters."
                />
            )}

            <Pager
                page={page}
                totalPages={rows.length === PAGE_SIZE ? page + 1 : page}
                onChange={(p) => goTo({ tab: 'cards', name: searchName || undefined, supertype: supertype || undefined, page: p })}
            />

            <EntityDetailPanel
                open={!!selected}
                title={selected ? `Card: ${selected.name}` : ''}
                onClose={() => setSelected(null)}
                sections={selected ? sectionsFor(selected) : []}
            />
        </div>
    );
};

export default CardsTab;
